import React, { useState, useEffect } from 'react';
import { 
  Plug, 
  CheckCircle2, 
  ExternalLink, 
  Settings, 
  Search,
  Zap,
  MessageSquare,
  Phone,
  CreditCard,
  Mail,
  Database,
  Cloud,
  Calendar,
  X,
  Loader2,
  AlertCircle,
  Key,
  Link2,
  Unlink,
  Bell,
  Send
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { integrationsAPI } from '../../services/api';

const ClientIntegrationsPage = () => {
  const { clientUser } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [availableIntegrations, setAvailableIntegrations] = useState([]);
  const [connectedIntegrations, setConnectedIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectModal, setConnectModal] = useState(null);
  const [settingsModal, setSettingsModal] = useState(null);
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [genesysRegion, setGenesysRegion] = useState('us-east-1');
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');
  
  // Genesys regions
  const genesysRegions = [
    { value: 'us-east-1', label: 'US East (Virginia)' },
    { value: 'us-west-2', label: 'US West (Oregon)' },
    { value: 'eu-west-1', label: 'EU West (Ireland)' },
    { value: 'eu-central-1', label: 'EU Central (Frankfurt)' },
    { value: 'ap-southeast-2', label: 'Asia Pacific (Sydney)' },
    { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' },
    { value: 'ca-central-1', label: 'Canada (Central)' },
  ];
  
  // Telegram settings state
  const [telegramChatId, setTelegramChatId] = useState('');
  const [telegramNotifications, setTelegramNotifications] = useState([]);
  const [savingSettings, setSavingSettings] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  // Icons map
  const iconMap = {
    Calendar: Calendar,
    CRM: Database,
    Communication: MessageSquare,
    Messaging: MessageSquare,
    'Contact Center': Phone,
    Custom: Settings,
    Database: Database,
  };

  // Color map
  const colorMap = {
    'cal-com': 'bg-blue-500',
    'calendly': 'bg-blue-400',
    'google-calendar': 'bg-red-500',
    'hubspot': 'bg-orange-500',
    'slack': 'bg-purple-500',
    'salesforce': 'bg-blue-600',
    'custom-api': 'bg-gray-500',
    'whatsapp': 'bg-green-500',
    'telegram': 'bg-sky-500',
    'genesys': 'bg-indigo-500',
  };

  // Static integrations (already connected via platform)
  const platformIntegrations = [
    {
      id: 'telnyx',
      name: 'Telnyx',
      description: 'Voice calls, SMS, and WebRTC communication',
      category: 'communication',
      icon: Phone,
      color: 'bg-green-500',
      connected: true,
      platform: true,
      features: ['Voice Calls', 'SMS', 'SIP Trunking']
    },
    {
      id: 'openai',
      name: 'OpenAI',
      description: 'GPT-powered conversational AI for voice agents',
      category: 'ai',
      icon: Zap,
      color: 'bg-emerald-500',
      connected: true,
      platform: true,
      features: ['GPT-4o', 'Speech-to-Text', 'Text-to-Speech']
    },
    {
      id: 'stripe',
      name: 'Stripe',
      description: 'Payment processing and subscription management',
      category: 'payments',
      icon: CreditCard,
      color: 'bg-purple-500',
      connected: true,
      platform: true,
      features: ['Payments', 'Subscriptions', 'Invoices']
    },
  ];

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const fetchIntegrations = async () => {
    try {
      setLoading(true);
      const clientId = clientUser?.id;
      
      const [availableRes, connectedRes] = await Promise.all([
        integrationsAPI.getAvailable(),
        clientId ? integrationsAPI.getConnected(clientId) : Promise.resolve({ data: { integrations: [] } })
      ]);
      
      setAvailableIntegrations(availableRes.data.integrations || []);
      setConnectedIntegrations(connectedRes.data.integrations || []);
    } catch (error) {
      console.error('Failed to fetch integrations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    if (!connectModal) return;
    
    setConnecting(true);
    setError('');
    const clientId = clientUser?.id;
    
    try {
      const credentials = {};
      if (connectModal.required_fields?.includes('api_key')) {
        credentials.api_key = apiKey;
      }
      if (connectModal.required_fields?.includes('api_secret')) {
        credentials.api_secret = apiSecret;
      }
      if (connectModal.required_fields?.includes('webhook_url')) {
        credentials.webhook_url = webhookUrl;
      }
      if (connectModal.required_fields?.includes('phone_number_id')) {
        credentials.phone_number_id = phoneNumberId;
      }
      
      // Add custom settings for Genesys
      if (connectModal.id === 'genesys') {
        credentials.custom_settings = { region: genesysRegion };
      }
      
      await integrationsAPI.connect(clientId, {
        integration_id: connectModal.id,
        credentials
      });
      
      // Refresh integrations list
      await fetchIntegrations();
      setConnectModal(null);
      setApiKey('');
      setApiSecret('');
      setWebhookUrl('');
      setPhoneNumberId('');
      setGenesysRegion('us-east-1');
    } catch (error) {
      console.error('Failed to connect integration:', error);
      setError(error.response?.data?.detail || 'Failed to connect. Please check your credentials.');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async (integrationId) => {
    if (!confirm('Are you sure you want to disconnect this integration?')) return;
    const clientId = clientUser?.id;
    
    try {
      await integrationsAPI.disconnect(clientId, integrationId);
      await fetchIntegrations();
    } catch (error) {
      console.error('Failed to disconnect:', error);
    }
  };

  const isConnected = (integrationId) => {
    return connectedIntegrations.some(i => i.id === integrationId);
  };

  // Telegram notification settings
  const openTelegramSettings = async (integration) => {
    setSettingsModal(integration);
    const clientId = clientUser?.id;
    
    try {
      const response = await integrationsAPI.telegram.getNotificationSettings(clientId);
      setTelegramChatId(response.data.default_chat_id || '');
      setTelegramNotifications(response.data.enabled_notifications || [
        'call_transferred', 'new_lead', 'campaign_completed'
      ]);
    } catch (error) {
      console.error('Failed to fetch Telegram settings:', error);
      // Set defaults
      setTelegramNotifications(['call_transferred', 'new_lead', 'campaign_completed']);
    }
  };

  const handleSaveTelegramSettings = async () => {
    if (!telegramChatId) {
      setError('Chat ID is required');
      return;
    }
    
    setSavingSettings(true);
    setError('');
    const clientId = clientUser?.id;
    
    try {
      await integrationsAPI.telegram.updateNotificationSettings(clientId, {
        default_chat_id: telegramChatId,
        enabled_notifications: telegramNotifications
      });
      setSettingsModal(null);
    } catch (error) {
      console.error('Failed to save settings:', error);
      setError(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleTestTelegramNotification = async () => {
    if (!telegramChatId) {
      setError('Please enter a Chat ID first');
      return;
    }
    
    setSendingTest(true);
    setError('');
    const clientId = clientUser?.id;
    
    try {
      await integrationsAPI.telegram.testNotification(clientId, telegramChatId);
      // Show success briefly
      setError('');
    } catch (error) {
      console.error('Failed to send test:', error);
      setError(error.response?.data?.detail || 'Failed to send test notification');
    } finally {
      setSendingTest(false);
    }
  };

  const toggleNotificationType = (type) => {
    if (telegramNotifications.includes(type)) {
      setTelegramNotifications(telegramNotifications.filter(t => t !== type));
    } else {
      setTelegramNotifications([...telegramNotifications, type]);
    }
  };

  // Combine platform and API integrations
  const allIntegrations = [
    ...platformIntegrations,
    ...availableIntegrations.map(int => ({
      ...int,
      icon: iconMap[int.category] || Plug,
      color: colorMap[int.id] || 'bg-gray-500',
      connected: isConnected(int.id),
      platform: false
    }))
  ];

  const categories = ['all', 'ai', 'communication', 'messaging', 'Calendar', 'CRM', 'payments'];
  
  const filteredIntegrations = allIntegrations.filter(integration => {
    const matchesSearch = integration.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          integration.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory === 'all' || 
                           integration.category?.toLowerCase() === activeCategory.toLowerCase();
    return matchesSearch && matchesCategory;
  });

  const connectedCount = allIntegrations.filter(i => i.connected).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Integrations</h1>
          <p className="text-gray-400 mt-1">Connect your favorite tools and services</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-lg">
            <span className="text-green-400 font-medium">{connectedCount} Connected</span>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search integrations..."
            className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg pl-10 pr-4 py-3 text-white placeholder-gray-500 outline-none focus:border-teal-400"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors capitalize ${
                activeCategory === category
                  ? 'bg-teal-400 text-gray-900'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
        </div>
      ) : (
        /* Integrations Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredIntegrations.map(integration => {
            const Icon = integration.icon;
            return (
              <div
                key={integration.id}
                className={`bg-[#0d1117] border rounded-xl p-5 transition-all hover:border-gray-600 ${
                  integration.connected ? 'border-green-500/30' : 'border-gray-800'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 ${integration.color} rounded-xl flex items-center justify-center`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">{integration.name}</h3>
                      <p className="text-gray-500 text-xs capitalize">{integration.category}</p>
                    </div>
                  </div>
                  {integration.connected && (
                    <div className="flex items-center gap-1 text-green-400 text-xs bg-green-400/10 px-2 py-1 rounded-full">
                      <CheckCircle2 className="w-3 h-3" />
                      Connected
                    </div>
                  )}
                </div>

                <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                  {integration.description}
                </p>

                {integration.features && (
                  <div className="flex flex-wrap gap-1 mb-4">
                    {integration.features.slice(0, 3).map((feature, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-0.5 bg-gray-800 text-gray-400 rounded text-xs"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                )}

                <div className="flex items-center gap-2">
                  {integration.platform ? (
                    <button
                      disabled
                      className="flex-1 py-2 bg-gray-700 text-gray-400 rounded-lg text-sm cursor-not-allowed"
                    >
                      Platform Managed
                    </button>
                  ) : integration.connected ? (
                    <>
                      <button
                        onClick={() => handleDisconnect(integration.id)}
                        className="flex-1 py-2 bg-red-500/10 text-red-400 rounded-lg text-sm hover:bg-red-500/20 transition-colors flex items-center justify-center gap-1"
                      >
                        <Unlink className="w-4 h-4" />
                        Disconnect
                      </button>
                      {integration.id === 'telegram' ? (
                        <button
                          onClick={() => openTelegramSettings(integration)}
                          className="p-2 bg-teal-400/10 text-teal-400 rounded-lg hover:bg-teal-400/20 transition-colors"
                          title="Configure Notifications"
                        >
                          <Bell className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          className="p-2 bg-gray-700 text-gray-400 rounded-lg hover:bg-gray-600 transition-colors"
                        >
                          <Settings className="w-4 h-4" />
                        </button>
                      )}
                    </>
                  ) : (
                    <button
                      onClick={() => setConnectModal(integration)}
                      className="flex-1 py-2 bg-teal-400 text-gray-900 rounded-lg text-sm font-medium hover:bg-teal-300 transition-colors flex items-center justify-center gap-1"
                    >
                      <Link2 className="w-4 h-4" />
                      Connect
                    </button>
                  )}
                  {integration.docs_url && (
                    <a
                      href={integration.docs_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 bg-gray-700 text-gray-400 rounded-lg hover:bg-gray-600 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Connect Modal */}
      {connectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 ${colorMap[connectModal.id] || 'bg-gray-500'} rounded-lg flex items-center justify-center`}>
                  {connectModal.category === 'Calendar' ? <Calendar className="w-5 h-5 text-white" /> : <Plug className="w-5 h-5 text-white" />}
                </div>
                <div>
                  <h3 className="text-white font-semibold">Connect {connectModal.name}</h3>
                  <p className="text-gray-500 text-xs">{connectModal.category}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setConnectModal(null);
                  setApiKey('');
                  setWebhookUrl('');
                  setError('');
                }}
                className="p-1 text-gray-500 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-gray-400 text-sm mb-4">
              {connectModal.description}
            </p>

            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <div className="space-y-4">
              {connectModal.required_fields?.includes('api_key') && (
                <div>
                  <label className="block text-gray-300 text-sm mb-2">
                    <Key className="w-4 h-4 inline mr-1" />
                    {connectModal.id === 'genesys' ? 'Client ID' : 'API Key'}
                  </label>
                  <input
                    type="password"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder={connectModal.id === 'genesys' ? "Enter your Genesys Client ID..." : "Enter your API key..."}
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 outline-none focus:border-teal-400"
                  />
                  {connectModal.docs_url && (
                    <p className="text-gray-500 text-xs mt-1">
                      Get your {connectModal.id === 'genesys' ? 'OAuth credentials' : 'API key'} from{' '}
                      <a href={connectModal.docs_url} target="_blank" rel="noopener noreferrer" className="text-teal-400 hover:underline">
                        {connectModal.name} dashboard
                      </a>
                    </p>
                  )}
                </div>
              )}

              {connectModal.required_fields?.includes('api_secret') && (
                <div>
                  <label className="block text-gray-300 text-sm mb-2">
                    <Key className="w-4 h-4 inline mr-1" />
                    {connectModal.id === 'genesys' ? 'Client Secret' : 'API Secret'}
                  </label>
                  <input
                    type="password"
                    value={apiSecret}
                    onChange={(e) => setApiSecret(e.target.value)}
                    placeholder={connectModal.id === 'genesys' ? "Enter your Genesys Client Secret..." : "Enter your API secret..."}
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 outline-none focus:border-teal-400"
                  />
                </div>
              )}

              {connectModal.id === 'genesys' && (
                <div>
                  <label className="block text-gray-300 text-sm mb-2">
                    <Cloud className="w-4 h-4 inline mr-1" />
                    Genesys Cloud Region
                  </label>
                  <select
                    value={genesysRegion}
                    onChange={(e) => setGenesysRegion(e.target.value)}
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-3 text-white outline-none focus:border-teal-400"
                  >
                    {genesysRegions.map(region => (
                      <option key={region.value} value={region.value}>
                        {region.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-gray-500 text-xs mt-1">
                    Select the region where your Genesys Cloud organization is hosted
                  </p>
                </div>
              )}

              {connectModal.required_fields?.includes('webhook_url') && (
                <div>
                  <label className="block text-gray-300 text-sm mb-2">
                    <Link2 className="w-4 h-4 inline mr-1" />
                    Webhook URL
                  </label>
                  <input
                    type="text"
                    value={webhookUrl}
                    onChange={(e) => setWebhookUrl(e.target.value)}
                    placeholder="https://hooks.slack.com/services/..."
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 outline-none focus:border-teal-400"
                  />
                </div>
              )}

              {connectModal.required_fields?.includes('phone_number_id') && (
                <div>
                  <label className="block text-gray-300 text-sm mb-2">
                    <Phone className="w-4 h-4 inline mr-1" />
                    Phone Number ID
                  </label>
                  <input
                    type="text"
                    value={phoneNumberId}
                    onChange={(e) => setPhoneNumberId(e.target.value)}
                    placeholder="Enter your WhatsApp Phone Number ID..."
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 outline-none focus:border-teal-400"
                  />
                  <p className="text-gray-500 text-xs mt-1">
                    Find this in your Meta Business Suite under WhatsApp Business API
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setConnectModal(null);
                  setApiKey('');
                  setApiSecret('');
                  setWebhookUrl('');
                  setPhoneNumberId('');
                  setGenesysRegion('us-east-1');
                  setError('');
                }}
                className="flex-1 py-3 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConnect}
                disabled={connecting || (!apiKey && !webhookUrl && !phoneNumberId && !apiSecret)}
                className={`flex-1 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                  connecting || (!apiKey && !webhookUrl && !phoneNumberId && !apiSecret)
                    ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                    : 'bg-teal-400 text-gray-900 hover:bg-teal-300'
                }`}
              >
                {connecting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    Connect
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Telegram Settings Modal */}
      {settingsModal && settingsModal.id === 'telegram' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-sky-500 rounded-lg flex items-center justify-center">
                  <Bell className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Telegram Notifications</h3>
                  <p className="text-gray-500 text-xs">Configure notification settings</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setSettingsModal(null);
                  setError('');
                }}
                className="p-1 text-gray-500 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <div className="space-y-5">
              {/* Chat ID */}
              <div>
                <label className="block text-gray-300 text-sm mb-2">
                  <MessageSquare className="w-4 h-4 inline mr-1" />
                  Telegram Chat ID
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={telegramChatId}
                    onChange={(e) => setTelegramChatId(e.target.value)}
                    placeholder="e.g., 7240159470"
                    className="flex-1 bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 outline-none focus:border-teal-400"
                  />
                  <button
                    onClick={handleTestTelegramNotification}
                    disabled={sendingTest || !telegramChatId}
                    className={`px-4 rounded-lg transition-colors flex items-center gap-1 ${
                      sendingTest || !telegramChatId
                        ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                        : 'bg-sky-500/20 text-sky-400 hover:bg-sky-500/30'
                    }`}
                    title="Send test notification"
                  >
                    {sendingTest ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </button>
                </div>
                <p className="text-gray-500 text-xs mt-1">
                  Message your bot on Telegram to get your Chat ID
                </p>
              </div>

              {/* Notification Types */}
              <div>
                <label className="block text-gray-300 text-sm mb-3">
                  <Bell className="w-4 h-4 inline mr-1" />
                  Notification Types
                </label>
                <div className="space-y-2">
                  {[
                    { id: 'call_transferred', label: 'Call Transfers', desc: 'When AI transfers to live agent' },
                    { id: 'new_lead', label: 'New Leads', desc: 'When a new lead is captured' },
                    { id: 'campaign_completed', label: 'Campaign Completed', desc: 'When a campaign finishes' },
                    { id: 'call_started', label: 'Call Started', desc: 'When a new call begins' },
                    { id: 'call_ended', label: 'Call Ended', desc: 'When a call completes' },
                  ].map(notifType => (
                    <label
                      key={notifType.id}
                      className="flex items-center justify-between p-3 bg-[#1a1a1f] border border-gray-700 rounded-lg cursor-pointer hover:border-gray-600 transition-colors"
                    >
                      <div>
                        <span className="text-white text-sm">{notifType.label}</span>
                        <p className="text-gray-500 text-xs">{notifType.desc}</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={telegramNotifications.includes(notifType.id)}
                        onChange={() => toggleNotificationType(notifType.id)}
                        className="w-5 h-5 rounded border-gray-600 text-teal-400 focus:ring-teal-400 focus:ring-offset-0 bg-gray-800"
                      />
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setSettingsModal(null);
                  setError('');
                }}
                className="flex-1 py-3 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTelegramSettings}
                disabled={savingSettings || !telegramChatId}
                className={`flex-1 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                  savingSettings || !telegramChatId
                    ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                    : 'bg-teal-400 text-gray-900 hover:bg-teal-300'
                }`}
              >
                {savingSettings ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    Save Settings
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientIntegrationsPage;
